import { Flex, Text, View, Button, Spinner, SimpleSelect, Menu, IconButton, IconVideoCameraLine, IconXLine } from '@smarterservices/smarter-ui';
import DualPane from '../../../DualPane';
import StepInstructions from '../../../Common/StepInstructions';
import { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../../../../stores/app';
import { useSecondCameraStore } from '../../../../stores/secondCamera';
import {EVENTS} from '@smarterservices/media-agent';
import Loader from '../../../Common/Loader';

const SecondCameraView = ({ stepData, setIsTaskComplete }) => {
  // Initialize platform logger
  
  const logContext = 'SecondCameraView';

  const CAMERA_STREAM_TIMEOUT = 5000; // 5 second timeout for video stream initialization

  const [isCameraViewInitialized, setIsCameraViewInitialized] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [selectedDeviceLabel, setSelectedDeviceLabel] = useState('');
  const [isVideoStreamLoading, setIsVideoStreamLoading] = useState(true);
  const [videoStreamError, setVideoStreamError] = useState('');
  
  const [isLoadingVideoDevices, setIsLoadingVideoDevices] = useState(true);
    
  const videoRef = useRef(null);
  const mediaStream = useRef(null);
  
  const { initialize, publishStream, changeCamera } = useSecondCameraStore();

  const isAppInitialized = useAppStore((state) => state.initialized);
  const isSecondCameraInitialized = useSecondCameraStore((state) => state.initialized);
  const videoStream = useSecondCameraStore((state) => state.videoStream);
  const videoDevices = useSecondCameraStore((state) => state.videoDevices);
  const mediaAgent = useSecondCameraStore((state) => state.mediaAgent);
  const isReplacingStream = useSecondCameraStore((state) => state.isReplacingStream);
  const currentDeviceId = useSecondCameraStore((state) => state.currentDeviceId);
  const currentDeviceLabel = useSecondCameraStore((state) => state.currentDeviceLabel);
    
 const handleCameraChange = async (deviceLabel) => {
  
  await changeCamera({
    label: deviceLabel,
    identity: useAppStore.getState().identity
  });
 }
    
  
  
  // Enumerate available devices when component mounts
  useEffect(() => {
    async function initializeCamera() {
      // get video devices
      setIsLoadingVideoDevices(true);
      
      await initialize();
           
      setIsLoadingVideoDevices(false);
      setIsVideoStreamLoading(false);

    }
    if(isAppInitialized && !isSecondCameraInitialized){
      initializeCamera();
    }
  }, [isAppInitialized]);

  // Update video stream when currentDeviceId changes
  useEffect(() => {
    if (currentDeviceId && videoRef.current) {
      // Get new stream with the updated device
      const updateVideoStream = async () => {
        try {
          
          // Stop current stream if exists
          if (mediaStream.current) {
            mediaStream.current.getTracks().forEach(track => track.stop());
          }
          
          // Get new stream with current device
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: currentDeviceId },
            },
            audio: false,
          });
          
          // Update video element
          videoRef.current.srcObject = newStream;
          mediaStream.current = newStream;
          
          console.log("Video stream updated for device:", currentDeviceLabel);
        } catch (error) {
          
          console.error("Error updating video stream:", error);
        }
      };
      updateVideoStream();
    }
  }, [currentDeviceId, cameraActive]);

  useEffect(() => {
    if(videoStream && !isVideoStreamLoading){
      if (videoRef.current) {
        videoRef.current.srcObject = videoStream;
      }
    }
  }, [videoStream, isVideoStreamLoading]);
  
  // Enable camera when active state changes or selected device changes
  useEffect(() => {
    if (cameraActive && videoRef.current && selectedDeviceId) {
      console.log("cameraActive && videoRef.current && selectedDeviceId");
      enableCamera();
    }
    
    // Cleanup function to stop camera when component unmounts
    return () => {
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, [cameraActive, selectedDeviceId]);


/**
   * Handle video stream initialization with timeout and error handling
   * Sets up event listeners for video element and manages loading states
   */
  useEffect(() => {
    let playTimeout;
    if(videoRef.current){
/*
        playTimeout = setTimeout(() => {
            setIsVideoStreamLoading(false);
            setVideoStreamError('Camera stream could not be obtained');           
        }, CAMERA_STREAM_TIMEOUT);
*/
        videoRef.current.onloadedmetadata = () => {
            videoRef.current.play()
            .then(async () => {
                clearTimeout(playTimeout); // Clear the timeout if video plays successfully
                setIsVideoStreamLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setVideoStreamError('Error loading camera stream.'); 
                clearTimeout(playTimeout);
                setIsVideoStreamLoading(false);
            });
        };

        // Handle case where video errors out
        videoRef.current.onerror = () => {
            setVideoStreamError('Error loading camera stream.'); 
            clearTimeout(playTimeout);
            setIsVideoStreamLoading(false);
        };
    }
    return () => {
      if (playTimeout) {
        clearTimeout(playTimeout);
        setVideoStreamError('');        
      }
    };
  }, [videoRef?.current?.srcObject]);

  
  // Function to enable the camera with the selected device
  const enableCamera = async () => {
    try {
      // Stop any existing stream first
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach(track => track.stop());
      }
      
      // Request camera access with the selected device
      const constraints = {
        video: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true,
        audio: true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Save stream reference for cleanup
      mediaStream.current = stream;
      
      // Connect stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;

         


      }
      
      // Set task as complete when camera is active
      setIsTaskComplete(true);
      setCameraError(null);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError(`Camera access error: ${error.message}`);
      setCameraActive(false);
    }
  };
  
  /**
   * Handles camera selection change from the dropdown
   * Stops previous stream and initializes a new one with the selected device
   * 
   * @param {Event} event - The change event
   * @param {Object} selected - The selected option data
   * @param {string} selected.id - The device ID of the selected camera
   * @param {string} selected.value - The label of the selected camera
   */
  const handleDeviceChange = async (label) => {
    const selectedDevice = videoDevices.find((device) => device.label === label);
    setSelectedDeviceLabel(label);
    setSelectedDeviceId(selectedDevice.deviceId);
    setIsVideoStreamLoading(true);
    
    if (videoStream) {
      // Stop the previous stream
      videoStream.getTracks().forEach((track) => track.stop());
    }

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: selectedDevice.deviceId },
        },
        audio: false,
      });
      
      setVideoStream(newStream);
      
      if (videoRef.current) {
        // Set the new stream as the video source
        videoRef.current.srcObject = newStream;  
      }
    } catch (error) {
      logger.logError(logContext, 'Error switching camera:', error);
      setVideoStreamError('Error switching camera. Please try again.'); 
      setIsVideoStreamLoading(false);
    }
  };



  return (
    
        <View 
        alignItems="center" 
        width="100vw" height="100vh">
          <View as="div" background='primary-inverse' padding="small">
            <Flex justifyItems="space-between" alignItems="center">
              <Text size="medium">Mobile Camera Setup</Text>
              <Flex gap="small">
                <Menu
                  trigger={
                    <IconButton
                      screenReaderLabel="Select Camera"
                      withBackground={true}
                      withBorder={false}
                      size="small"
                    >
                      <IconVideoCameraLine />
                    </IconButton>
                  }
                >
                  {videoDevices.map(device => (
                    <Menu.Item
                      key={device.deviceId}
                      selected={selectedDeviceLabel === device.label}
                      onSelect={async() => await handleCameraChange(device.label || `Camera ${videoDevices.indexOf(device) + 1}`)}
                    >
                      {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                    </Menu.Item>
                  ))}
                </Menu>
                {publishing && (
                  <Button
                    color="danger"
                    size="small"
                    onClick={() => {
                      if (videoStream) {
                        videoStream.getTracks().forEach((track) => track.stop());
                        setVideoStream(null);
                        if (videoRef.current) videoRef.current.srcObject = null;
                        
                        // Reset states
                        setIsVideoStreamLoading(false);
                        setVideoStreamError('');
                        
                        // Attempt to restart camera after a brief delay
                        setTimeout(() => {
                          getVideoDevices();
                        }, 500);
                      }
                    }}
                  >
                  Stop Video
                </Button>
              )}
              </Flex>
            </Flex>
          </View>
            {isVideoStreamLoading && (
              <Flex direction="column" alignItems="center" padding="medium" height="100%">
                <Loader text="Enabling camera..." />
              </Flex>
            )}

            {isReplacingStream && (
              <Flex direction="column" alignItems="center" padding="medium" height="100%">
                <Loader text="Changing camera..." />
              </Flex>
            )}

            <View as="div" width="100%" textAlign="center">
              <View as="div" margin="none" position="relative">
                 <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  style={{ 
                    width: '100%', 
                    height: 'calc(100vh - 120px)',
                    objectFit: 'contain',
                    //transform: 'scaleX(-1)', // Mirror the video for selfie view
                    backgroundColor: '#000'
                  }}
                />

              </View>
            </View>
          
          
          {cameraError && (
            <Text color="danger">{cameraError}</Text>
          )}
        </View>
      
  );
};

export default SecondCameraView;